import { useEffect, useState, type ReactNode } from "react";
import { Link, NavLink, Outlet, useLocation, useNavigate } from "react-router";
import { useAuth } from "./auth.tsx";
import { useLive } from "./live.tsx";
import { useApi } from "./api.ts";
import { Button, cn, Icon, IconButton, type IconName } from "./ui/index.tsx";
import { LogoMark, Wordmark } from "./ui/logo.tsx";
import type { TicketSummary } from "../shared/types.ts";

export function Logo({ className, animated = true }: { className?: string; animated?: boolean }) {
  return (
    <Link to="/" className={cn("group flex items-center gap-2.5", className)} aria-label="ExoWare home">
      <LogoMark animated={animated} className="size-8 drop-shadow-[0_4px_14px_rgb(255_61_79/0.35)] transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-105" />
      <Wordmark />
    </Link>
  );
}

/** Ambient background shared by public and auth pages: aurora glow, grid floor, slow scanline. */
export function Atmosphere() {
  return (
    <div className="pointer-events-none fixed inset-0 -z-10 overflow-hidden" aria-hidden>
      <div className="grid-floor absolute inset-x-0 top-0 h-[70vh]" />
      <div className="absolute left-1/2 top-[-18rem] h-[38rem] w-[70rem] -translate-x-1/2 rounded-full bg-accent/[0.13] blur-[140px]" />
      <div className="absolute right-[-12rem] top-[22vh] h-[32rem] w-[32rem] rounded-full bg-[#3a2fb8]/[0.12] blur-[130px]" />
      <div className="absolute inset-x-0 top-0 h-[40vh] animate-scan bg-gradient-to-b from-transparent via-accent/[0.025] to-transparent" />
    </div>
  );
}

// ── Public pages (landing, games, pricing, status) ──

const PUBLIC_LINKS = [{ to: "/games", label: "Games" }, { to: "/pricing", label: "Pricing" }, { to: "/status", label: "Status" }];

export function PublicLayout() {
  const { me } = useAuth();
  const location = useLocation();
  const [menu, setMenu] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => setMenu(false), [location.pathname]);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const linkClass = ({ isActive }: { isActive: boolean }) =>
    cn("rounded-full px-3.5 py-1.5 transition-colors hover:text-fg", isActive ? "bg-white/[0.06] text-fg" : "text-muted");
  return (
    <div className="relative flex min-h-screen flex-col overflow-x-clip">
      <Atmosphere />
      <header className="sticky top-0 z-30 px-4 pt-4">
        <div className={cn("mx-auto flex h-14 max-w-6xl items-center justify-between rounded-2xl border px-4 transition-all duration-300",
          scrolled ? "border-line bg-bg/70 shadow-[0_12px_40px_-12px_rgb(0_0_0/0.8)] backdrop-blur-xl" : "border-transparent")}>
          <Logo />
          <nav className="flex items-center gap-1 text-sm">
            <div className="mr-2 hidden items-center gap-0.5 rounded-full border border-line/70 bg-surface/50 p-1 backdrop-blur sm:flex">
              {PUBLIC_LINKS.map((l) => <NavLink key={l.to} to={l.to} className={linkClass}>{l.label}</NavLink>)}
            </div>
            {me ? (
              <Link to="/home"><Button variant="primary" size="sm">Open dashboard</Button></Link>
            ) : (
              <>
                <Link to="/login" className="hidden rounded-lg px-3 py-2 text-muted hover:text-fg sm:block">Sign in</Link>
                <Link to="/register"><Button variant="primary" size="sm">Get started</Button></Link>
              </>
            )}
            <IconButton icon={menu ? "x" : "menu"} label="Menu" className="ml-1 sm:hidden" onClick={() => setMenu((m) => !m)} />
          </nav>
        </div>
        {menu && (
          <nav className="pane mx-auto mt-2 flex max-w-6xl flex-col p-2 text-sm sm:hidden">
            {PUBLIC_LINKS.map((l) => <NavLink key={l.to} to={l.to} className={linkClass}>{l.label}</NavLink>)}
            {!me && <NavLink to="/login" className={linkClass}>Sign in</NavLink>}
          </nav>
        )}
      </header>
      <main className="flex-1"><Outlet /></main>
      <footer className="mt-24 border-t border-line/70">
        <div className="mx-auto flex max-w-6xl flex-wrap items-center justify-between gap-6 px-6 py-10">
          <div className="flex items-center gap-3">
            <LogoMark animated={false} className="size-7 opacity-80" />
            <span className="kicker">© {new Date().getFullYear()} ExoWare // all systems nominal</span>
          </div>
          <div className="flex gap-5 text-sm text-faint">
            <Link to="/games" className="hover:text-fg">Games</Link>
            <Link to="/pricing" className="hover:text-fg">Pricing</Link>
            <Link to="/status" className="hover:text-fg">Status</Link>
            <Link to="/login" className="hover:text-fg">Sign in</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

/** Centered card layout for login/register. */
export function AuthLayout({ title, subtitle, children, footer }: { title: string; subtitle?: ReactNode; children: ReactNode; footer?: ReactNode }) {
  return (
    <div className="relative grid min-h-screen place-items-center overflow-hidden px-4 py-12">
      <Atmosphere />
      <div className="relative w-full max-w-sm animate-rise">
        <div className="mb-8 flex flex-col items-center gap-4">
          <Link to="/" aria-label="ExoWare home"><LogoMark className="size-16 drop-shadow-[0_0_30px_rgb(255_61_79/0.45)]" /></Link>
          <Wordmark className="text-lg" />
        </div>
        <div className="pane hud hud-accent glow-border bg-surface/90 p-6 backdrop-blur-xl">
          <div className="kicker mb-3">// secure channel</div>
          <h1 className="text-2xl font-semibold">{title}</h1>
          {subtitle && <p className="mt-1 text-sm text-muted">{subtitle}</p>}
          <div className="mt-6">{children}</div>
        </div>
        {footer && <div className="mt-6 text-center text-sm text-muted">{footer}</div>}
      </div>
    </div>
  );
}

// ── Signed-in app shell ──

interface NavItem { to: string; label: string; icon: IconName; end?: boolean; badge?: number }

const USER_NAV: NavItem[] = [
  { to: "/home", label: "Home", icon: "home" },
  { to: "/licenses", label: "Licenses", icon: "key" },
  { to: "/downloads", label: "Downloads", icon: "download" },
  { to: "/guides", label: "Guides", icon: "book" },
  { to: "/support", label: "Support", icon: "lifebuoy" },
  { to: "/account", label: "Account", icon: "user" },
];

const ADMIN_NAV: NavItem[] = [
  { to: "/admin", label: "Overview", icon: "chart", end: true },
  { to: "/admin/users", label: "Users", icon: "users" },
  { to: "/admin/keys", label: "License keys", icon: "key" },
  { to: "/admin/tickets", label: "Tickets", icon: "ticket" },
  { to: "/admin/content", label: "Content", icon: "megaphone" },
  { to: "/admin/releases", label: "Releases", icon: "package" },
  { to: "/admin/games", label: "Games", icon: "gamepad" },
  { to: "/admin/incidents", label: "Status & incidents", icon: "activity" },
  { to: "/admin/payments", label: "Payments", icon: "card" },
  { to: "/admin/diagnostics", label: "Diagnostics", icon: "bug" },
  { to: "/admin/audit", label: "Audit log", icon: "scroll" },
  { to: "/admin/settings", label: "Settings", icon: "settings" },
];

function NavGroup({ title, items, onNavigate }: { title?: string; items: NavItem[]; onNavigate: () => void }) {
  return (
    <div>
      {title && <div className="kicker flex items-center gap-2 px-3 pb-2 pt-6 text-faint"><span className="h-px w-3 bg-accent/60" />{title}</div>}
      <ul className="space-y-0.5">
        {items.map((item) => (
          <li key={item.to}>
            <NavLink to={item.to} end={item.end} onClick={onNavigate}
              className={({ isActive }) => cn(
                "group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-all",
                isActive
                  ? "bg-gradient-to-r from-accent/[0.16] to-transparent text-fg before:absolute before:inset-y-1.5 before:left-0 before:w-[2px] before:rounded-full before:bg-accent before:shadow-[0_0_12px_var(--color-accent)]"
                  : "text-muted hover:bg-white/[0.04] hover:text-fg",
              )}>
              <Icon name={item.icon} className="size-4 transition-transform group-hover:scale-110" />
              <span className="flex-1">{item.label}</span>
              {!!item.badge && <span className="rounded-full bg-accent px-1.5 text-[10px] font-semibold leading-4 text-accent-fg tabular-nums">{item.badge}</span>}
            </NavLink>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function AppLayout() {
  const { me, isAdmin, logout } = useAuth();
  const { connected, online } = useLive();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const openTickets = useApi<TicketSummary[]>(isAdmin ? "/admin/tickets?status=open" : null);

  useEffect(() => setOpen(false), [location.pathname]);

  const adminNav = ADMIN_NAV.map((n) => (n.to === "/admin/tickets" ? { ...n, badge: openTickets.data?.length } : n));
  const onlineUsers = new Set(online.map((o) => o.user_id)).size;

  const sidebar = (
    <div className="flex h-full flex-col">
      <div className="flex h-16 items-center px-5"><Logo /></div>
      <div className="mx-5 mb-2 flex items-center justify-between rounded-md border border-line/70 bg-bg/50 px-2.5 py-1.5 font-mono text-[10px] uppercase tracking-[0.14em] text-faint">
        <span className="flex items-center gap-1.5">
          <span className={cn("size-1.5 rounded-full", connected ? "bg-ok shadow-[0_0_8px_var(--color-ok)] animate-pulse-soft" : "bg-faint")} />
          {connected ? "link up" : "link down"}
        </span>
        <span>{isAdmin ? `${onlineUsers} online` : "secure"}</span>
      </div>
      <nav className="flex-1 overflow-y-auto px-3 pb-4">
        <NavGroup items={USER_NAV} onNavigate={() => setOpen(false)} />
        {isAdmin && <NavGroup title="Admin" items={adminNav} onNavigate={() => setOpen(false)} />}
      </nav>
      <div className="border-t border-line p-3">
        <div className="flex items-center gap-3 rounded-lg px-2 py-2">
          <div className="grid size-9 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-accent/30 to-ember/20 font-display text-xs font-semibold uppercase ring-1 ring-accent/30">{me?.username.slice(0, 2)}</div>
          <div className="min-w-0 flex-1">
            <div className="truncate text-sm font-medium">{me?.username}</div>
            <div className="flex items-center gap-1.5 text-[11px] text-faint">
              <span className={cn("size-1.5 rounded-full", connected ? "bg-ok" : "bg-faint")} />
              {isAdmin ? "Administrator" : "Member"}
            </div>
          </div>
          <IconButton icon="logout" label="Sign out" onClick={async () => { await logout(); navigate("/login"); }} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative min-h-screen lg:pl-64">
      <div className="pointer-events-none fixed inset-0 -z-10" aria-hidden>
        <div className="absolute right-[-10rem] top-[-14rem] h-[30rem] w-[44rem] rounded-full bg-accent/[0.07] blur-[140px]" />
        <div className="grid-floor absolute inset-x-0 top-0 h-[45vh] opacity-60" />
      </div>
      <aside className="fixed inset-y-0 left-0 z-30 hidden w-64 border-r border-line bg-surface/70 backdrop-blur-xl lg:block">{sidebar}</aside>
      {open && (
        <div className="fixed inset-0 z-40 lg:hidden">
          <div className="absolute inset-0 bg-black/60" onClick={() => setOpen(false)} />
          <aside className="relative h-full w-64 border-r border-line bg-surface/95 backdrop-blur-xl">{sidebar}</aside>
        </div>
      )}
      <header className="sticky top-0 z-20 flex h-14 items-center gap-3 border-b border-line bg-bg/80 px-4 backdrop-blur lg:hidden">
        <IconButton icon="menu" label="Open menu" onClick={() => setOpen(true)} />
        <Logo />
      </header>
      <main className="px-4 py-8 sm:px-8">
        <Outlet />
      </main>
    </div>
  );
}
